// @ts-nocheck
// Invoice/Bill Service
// CRUD operations and business logic for invoices
// Offline-first: Always save locally first, sync silently when online
// OPTIMIZED: Uses Firestore query optimization and caching

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  limit as firestoreLimit,
  startAfter,
  runTransaction,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
  DocumentSnapshot,
  Unsubscribe
} from 'firebase/firestore'
import { app, auth, db, COLLECTIONS, getFirebaseReadinessErrorMessage, isFirebaseReady, waitForAuthenticatedUser } from './firebase'
import type { Invoice, ScannedInvoiceData } from '../types'
import { findOrCreatePartyFromInvoice } from './partyService'
import { findOrCreateItemFromInvoice, updateItemStock } from './itemService'
import { calculateGST, calculateInvoiceTotals } from './taxCalculations'
import { createInvoiceLedgerEntry } from './ledgerService'
import {
  saveToOffline,
  getAllFromOffline,
  getFromOffline,
  deleteFromOffline,
  addToSyncQueue,
  registerSyncCallback,
  isDeviceOnline,
  STORES
} from './offlineSyncService'
import { getFromCache, setInCache, invalidateCacheByPrefix } from '../utils/cacheUtils'
import { getCurrentCompanyId, getCurrentUserInfo, getScopedStorageKey } from '../utils/authUtils'
import { getUserAccessSettings } from './settingsService'
import { debugLog } from '../utils/debugLog'
import { DATA_EVENTS, emitDataEvent } from '../utils/dataEvents'
import { startBlocking } from '../utils/blockingStore'
import { getLocalJSON, getMutableLocalJSON, markLocalJSONDirty } from '../utils/localJsonStore'
import { normalizeInvoicePaymentFields } from '../utils/paymentStatus'
export type { Invoice } from '../types'

/**
 * Recursively removes undefined values from an object
 * Firebase doesn't accept undefined values in documents
 */
function sanitizeForFirebase(obj: any): any {
  if (obj === null || obj === undefined) {
    return null
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForFirebase(item)).filter(item => item !== undefined)
  }

  if (typeof obj === 'object' && obj !== null) {
    const sanitized: any = {}
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        const sanitizedValue = sanitizeForFirebase(value)
        if (sanitizedValue !== undefined) {
          sanitized[key] = sanitizedValue
        }
      }
    }
    return sanitized
  }

  return obj
}

const LOCAL_STORAGE_KEY = 'thisai_crm_invoices'
const getLocalStorageKey = () => getScopedStorageKey(LOCAL_STORAGE_KEY, 'company')

// Duplicate-submit guard for slow UI/network conditions.
// Prevents the same invoice create action from being inserted twice when users click repeatedly.
const INVOICE_CREATE_DEDUP_WINDOW_MS = 8000
const inFlightInvoiceCreateKeys = new Set<string>()
const recentCreatedInvoices = new Map<string, { invoice: Invoice; timestamp: number }>()

const normalizeDedupPart = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase()

const normalizeMoneyForDedup = (value: unknown): string => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0.00'
  return (Math.round(n * 100) / 100).toFixed(2)
}

const getMillis = (value: unknown): number => {
  const ms = new Date(String(value ?? '')).getTime()
  return Number.isFinite(ms) ? ms : 0
}

const isOfflineInvoiceId = (id: string): boolean =>
  id.startsWith('invoice_') || id.startsWith('offline_')

const getInvoiceIdentityKey = (
  invoiceData: Partial<Invoice>,
  companyIdOverride?: string
): string => {
  const companyId = normalizeDedupPart((invoiceData as any)?.companyId || companyIdOverride || '')
  const type = normalizeDedupPart((invoiceData as any)?.type)
  const invoiceNumber = normalizeDedupPart((invoiceData as any)?.invoiceNumber)

  if (invoiceNumber) {
    return [companyId, type, invoiceNumber].join('|')
  }

  return [
    companyId,
    type,
    normalizeDedupPart((invoiceData as any)?.partyId || (invoiceData as any)?.partyName),
    normalizeDedupPart((invoiceData as any)?.invoiceDate || (invoiceData as any)?.date),
    normalizeMoneyForDedup((invoiceData as any)?.grandTotal ?? (invoiceData as any)?.total ?? 0)
  ].join('|')
}

const choosePreferredInvoice = (
  current: Invoice,
  candidate: Invoice
): Invoice => {
  const currentId = String((current as any)?.id || '')
  const candidateId = String((candidate as any)?.id || '')
  const currentOffline = isOfflineInvoiceId(currentId)
  const candidateOffline = isOfflineInvoiceId(candidateId)

  if (currentOffline !== candidateOffline) {
    return candidateOffline ? current : candidate
  }

  const currentUpdated = getMillis((current as any)?.updatedAt || (current as any)?.createdAt || (current as any)?.invoiceDate)
  const candidateUpdated = getMillis((candidate as any)?.updatedAt || (candidate as any)?.createdAt || (candidate as any)?.invoiceDate)

  if (candidateUpdated > currentUpdated) return candidate
  if (candidateUpdated < currentUpdated) return current

  if ((current as any)?._pendingSync && !(candidate as any)?._pendingSync) return candidate
  if (!(current as any)?._pendingSync && (candidate as any)?._pendingSync) return current

  return current
}

const dedupeInvoicesByIdentity = (
  invoices: Invoice[],
  companyIdOverride?: string
): Invoice[] => {
  const byIdentity = new Map<string, Invoice>()
  for (const invoice of invoices) {
    const key = getInvoiceIdentityKey(invoice as Partial<Invoice>, companyIdOverride)
    const existing = byIdentity.get(key)
    if (!existing) {
      byIdentity.set(key, invoice)
      continue
    }
    byIdentity.set(key, choosePreferredInvoice(existing, invoice))
  }
  return Array.from(byIdentity.values())
}

const buildInvoiceCreateDedupKey = (
  invoiceData: Partial<Invoice>,
  companyId: string
): string => {
  const total = (invoiceData as any)?.grandTotal ?? (invoiceData as any)?.total ?? 0
  const paid = (invoiceData as any)?.payment?.paidAmount ?? (invoiceData as any)?.paidAmount ?? 0

  return [
    companyId,
    normalizeDedupPart((invoiceData as any)?.type),
    normalizeDedupPart((invoiceData as any)?.invoiceNumber),
    normalizeDedupPart((invoiceData as any)?.partyId || (invoiceData as any)?.partyName),
    normalizeDedupPart((invoiceData as any)?.invoiceDate || (invoiceData as any)?.date),
    normalizeMoneyForDedup(total),
    normalizeMoneyForDedup(paid)
  ].join('|')
}

const purgeRecentCreatedInvoiceCache = () => {
  const now = Date.now()
  for (const [key, entry] of recentCreatedInvoices.entries()) {
    if (now - entry.timestamp > INVOICE_CREATE_DEDUP_WINDOW_MS) {
      recentCreatedInvoices.delete(key)
    }
  }
}

const findRecentExistingInvoiceByDedupKey = (
  dedupKey: string,
  companyId: string
): Invoice | null => {
  purgeRecentCreatedInvoiceCache()
  const cached = recentCreatedInvoices.get(dedupKey)
  if (cached) {
    return cached.invoice
  }

  const now = Date.now()
  const localInvoices = getInvoicesFromLocalStorage()
  const matched = localInvoices.find(inv => {
    const invKey = buildInvoiceCreateDedupKey(inv as Partial<Invoice>, companyId)
    if (invKey !== dedupKey) return false

    const createdMs = getMillis((inv as any)?.createdAt || (inv as any)?.updatedAt || (inv as any)?.invoiceDate)
    return createdMs > 0 && (now - createdMs) <= INVOICE_CREATE_DEDUP_WINDOW_MS
  })

  return matched || null
}

const waitForRecentInvoiceByDedupKey = async (
  dedupKey: string,
  companyId: string,
  timeoutMs: number = 3000
): Promise<Invoice | null> => {
  const started = Date.now()
  while (Date.now() - started < timeoutMs) {
    const found = findRecentExistingInvoiceByDedupKey(dedupKey, companyId)
    if (found) return found
    await new Promise(resolve => setTimeout(resolve, 120))
  }
  return null
}

// Helper: filter invoices based on user role
// - Cashier: only sees their own invoices (createdByUserId matches)
// - Manager/Admin: sees all company invoices
const filterByUserRole = (invoices: Invoice[], userInfo: { uid: string; role: string } | null): Invoice[] => {
  if (!userInfo) return invoices

  // Admin and Manager see all invoices
  if (
    userInfo.role === 'admin' ||
    userInfo.role === 'org_admin' ||
    userInfo.role === 'super_admin' ||
    userInfo.role === 'manager'
  ) {
    return invoices
  }

  // Staff/Cashier/Sales visibility is configurable
  if (userInfo.role === 'staff' || userInfo.role === 'cashier' || userInfo.role === 'sales') {
    const visibility = getUserAccessSettings().staffInvoiceVisibility
    if (visibility === 'company') {
      return invoices
    }

    return invoices.filter(inv => {
      const createdBy = (inv as any).createdByUserId
      return createdBy === userInfo.uid
    })
  }

  return invoices
}

// Pagination interface for scalable data fetching
export interface PaginationOptions {
  limit?: number
  offset?: number
  cursor?: string // For cursor-based pagination
  sortBy?: 'invoiceDate' | 'createdAt' | 'invoiceNumber'
  sortOrder?: 'asc' | 'desc'
}

export interface PaginatedResult<T> {
  data: T[]
  total: number
  hasMore: boolean
  nextCursor?: string
  prevCursor?: string
}

// Store last document for cursor-based pagination
let lastDocumentSnapshot: DocumentSnapshot | null = null

/**
 * Get all invoices (with optional type filter) from Firestore.
 * OPTIMIZED: Uses Firestore query with where() and orderBy() for server-side filtering
 */
export async function getInvoices(type?: 'sale' | 'purchase' | 'quote', limit?: number): Promise<Invoice[]> {
  const companyId = getCurrentCompanyId()
  const userInfo = getCurrentUserInfo()

  if (!companyId) {
    console.warn('[getInvoices] Missing companyId - returning empty list')
    return []
  }
  const requestedLimit = limit || 100 // Default limit for better performance

  // Check cache only for actual offline sessions. Online Sales/POS must use Firestore.
  const cacheKey = type ? `invoices_${type}_${companyId}` : `invoices_all_${companyId}`
  const cached = getFromCache<Invoice[]>(cacheKey, companyId)
  if (cached && !isDeviceOnline()) {
    debugLog('[getInvoices] Cache hit:', cached.length, 'invoices')
    return filterByUserRole(cached, userInfo)
  }

  // STEP 1: Always try IndexedDB first (instant, works offline)
  let localInvoices: Invoice[] = []
  try {
    localInvoices = await getAllFromOffline<Invoice>(STORES.INVOICES)
    // Filter by type and companyId locally
    localInvoices = localInvoices.filter(inv => {
      const matchesType = !type || inv.type === type
      const matchesCompany = (inv as any).companyId === companyId
      return matchesType && matchesCompany
    })
    // Sort by invoiceDate descending (single sort)
    localInvoices.sort((a, b) => {
      const dateA = new Date((a as any).invoiceDate || (a as any).createdAt || 0).getTime()
      const dateB = new Date((b as any).invoiceDate || (b as any).createdAt || 0).getTime()
      return dateB - dateA
    })
    localInvoices = dedupeInvoicesByIdentity(localInvoices, companyId)
    debugLog('[getInvoices] Found', localInvoices.length, 'invoices in IndexedDB')
  } catch (error) {
    console.warn('[getInvoices] IndexedDB read failed, trying localStorage:', error)
    localInvoices = getInvoicesFromLocalStorage(type, limit)
  }

  // STEP 2: In production, do not silently replace Firestore with local/demo data.
  if (!isFirebaseReady()) {
    throw new Error(getFirebaseReadinessErrorMessage())
  }

  if (!isDeviceOnline()) {
    throw new Error('You are offline. Connect to the internet to load shared sales bills.')
  }

  // STEP 3: If online, fetch from Firebase with OPTIMIZED query
  try {
    const invoicesRef = collection(db!, COLLECTIONS.INVOICES)
    let q

    // Build optimized query with server-side filtering
    if (companyId && type) {
      // Use composite index: companyId + type + invoiceDate
      q = query(
        invoicesRef,
        where('companyId', '==', companyId),
        where('type', '==', type),
        orderBy('invoiceDate', 'desc'),
        firestoreLimit(requestedLimit)
      )
    } else if (companyId) {
      // Use index: companyId + invoiceDate
      q = query(
        invoicesRef,
        where('companyId', '==', companyId),
        orderBy('invoiceDate', 'desc'),
        firestoreLimit(requestedLimit)
      )
    } else {
      // Fallback: no company filter (for legacy/migration)
      q = query(
        invoicesRef,
        orderBy('invoiceDate', 'desc'),
        firestoreLimit(requestedLimit)
      )
    }

    console.info('[Sales Firebase Debug] getInvoices Firestore fetch', {
      authUid: auth?.currentUser?.uid || null,
      companyId,
      businessId: companyId,
      firebaseReady: isFirebaseReady(),
      firestoreDb: db,
      salesCollectionPath: type
        ? `${COLLECTIONS.INVOICES} where companyId == "${companyId}" and type == "${type}"`
        : `${COLLECTIONS.INVOICES} where companyId == "${companyId}"`,
      requestedLimit
    })
    debugLog('[getInvoices] Fetching with optimized query...')
    const snapshot = await getDocs(q)
    let serverInvoices = snapshot.docs.map(docToInvoice)

    // Store last document for pagination
    if (snapshot.docs.length > 0) {
      lastDocumentSnapshot = snapshot.docs[snapshot.docs.length - 1]
    }

    debugLog('[getInvoices] Optimized query returned:', serverInvoices.length, 'invoices')

    // Role-based filtering (still needed for cashier restriction)
    if (userInfo) {
      serverInvoices = filterByUserRole(serverInvoices, userInfo)
    }
    serverInvoices = dedupeInvoicesByIdentity(serverInvoices, companyId)

    // Firestore is the production source of truth. Do not merge browser-local
    // invoices into live Sales/POS reads.
    const localOnlyInvoices: Invoice[] = []
    let mergedInvoices = dedupeInvoicesByIdentity(serverInvoices, companyId)

    // Final sort (single sort after merge)
    mergedInvoices.sort((a, b) => {
      const dateA = new Date((a as any).invoiceDate || (a as any).createdAt || 0).getTime()
      const dateB = new Date((b as any).invoiceDate || (b as any).createdAt || 0).getTime()
      return dateB - dateA
    })

    // Apply final limit
    const result = requestedLimit ? mergedInvoices.slice(0, requestedLimit) : mergedInvoices

    // Update cache
    setInCache(cacheKey, result, companyId)

    // Update local cache with server data (in background, don't await)
    serverInvoices.forEach(invoice => {
      saveToOffline(STORES.INVOICES, invoice).catch(() => {})
    })

    debugLog('☁️ Retrieved:', result.length, 'invoices (server:', serverInvoices.length, ', local-only:', localOnlyInvoices.length, ')')
    return result
  } catch (error: any) {
    // If query fails (e.g., missing index), fall back to simple query
    if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
      console.warn('[getInvoices] Index missing, falling back to client-side filtering')
      return getInvoicesLegacy(type, limit)
    }
    console.error('FIRESTORE ERROR CODE:', error?.code || null)
    console.error('FIRESTORE ERROR MESSAGE:', error?.message || String(error))
    console.error('FULL ERROR:', error)
    console.warn('[getInvoices] Firebase fetch failed:', error)
    throw error
  }
}

/**
 * Legacy fallback for getInvoices when composite index is missing
 * Uses client-side filtering (slower but always works)
 */
async function getInvoicesLegacy(type?: 'sale' | 'purchase' | 'quote', limit?: number): Promise<Invoice[]> {
  const companyId = getCurrentCompanyId()
  const userInfo = getCurrentUserInfo()
  const requestedLimit = limit || 100

  if (!companyId) {
    console.warn('[getInvoicesLegacy] Missing companyId - returning empty list')
    return []
  }

  try {
    const invoicesRef = collection(db!, COLLECTIONS.INVOICES)
    // Rules-safe fallback: still scope to companyId, then filter client-side.
    const snapshot = await getDocs(query(invoicesRef, where('companyId', '==', companyId)))
    let serverInvoices = snapshot.docs.map(docToInvoice)

    // Client-side filtering
    serverInvoices = serverInvoices.filter(inv => {
      const matchesType = !type || inv.type === type
      return matchesType
    })

    // Role-based filtering
    if (userInfo) {
      serverInvoices = filterByUserRole(serverInvoices, userInfo)
    }
    serverInvoices = dedupeInvoicesByIdentity(serverInvoices, companyId)

    // Sort and limit
    serverInvoices.sort((a, b) => {
      const dateA = new Date((a as any).invoiceDate || (a as any).createdAt || 0).getTime()
      const dateB = new Date((b as any).invoiceDate || (b as any).createdAt || 0).getTime()
      return dateB - dateA
    })

    return serverInvoices.slice(0, requestedLimit)
  } catch (error) {
    console.error('[getInvoicesLegacy] Failed:', error)
    return []
  }
}

export function subscribeInvoicesForCompany(
  companyId: string,
  type: 'sale' | 'purchase' | 'quote' | undefined,
  onInvoices: (invoices: Invoice[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const salesCollectionPath = type
    ? `${COLLECTIONS.INVOICES} where companyId == "${companyId}" and type == "${type}"`
    : `${COLLECTIONS.INVOICES} where companyId == "${companyId}"`

  if (!companyId) {
    const error = new Error('Missing companyId')
    console.error('[Sales Firebase Debug] Invoice subscription skipped', {
      reason: error.message,
      authUid: auth?.currentUser?.uid || null,
      salesCollectionPath
    })
    onError?.(error)
    return () => {}
  }

  if (!isFirebaseReady() || !db) {
    const error = new Error(getFirebaseReadinessErrorMessage())
    console.error('[Sales Firebase Debug] Invoice subscription skipped', {
      reason: error.message,
      authUid: auth?.currentUser?.uid || null,
      companyId,
      firebaseReady: isFirebaseReady(),
      salesCollectionPath
    })
    onError?.(error)
    return () => {}
  }

  const constraints: any[] = [
    where('companyId', '==', companyId)
  ]
  if (type) {
    constraints.push(where('type', '==', type))
  }

  console.info('[Sales Firebase Debug] Invoice subscription starting', {
    authUid: auth?.currentUser?.uid || null,
    companyId,
    businessId: companyId,
    firebaseReady: isFirebaseReady(),
    firestoreDb: db,
    salesCollectionPath
  })

  return onSnapshot(
    query(collection(db, COLLECTIONS.INVOICES), ...constraints),
    (snapshot) => {
      let serverInvoices = snapshot.docs.map(docToInvoice)
      const userInfo = getCurrentUserInfo()
      if (userInfo) {
        serverInvoices = filterByUserRole(serverInvoices, userInfo)
      }
      serverInvoices = dedupeInvoicesByIdentity(serverInvoices, companyId)
      serverInvoices.sort((a, b) => {
        const dateA = new Date((a as any).invoiceDate || (a as any).createdAt || 0).getTime()
        const dateB = new Date((b as any).invoiceDate || (b as any).createdAt || 0).getTime()
        return dateB - dateA
      })

      console.info('[Sales Firebase Debug] Invoice snapshot received', {
        authUid: auth?.currentUser?.uid || null,
        companyId,
        salesCollectionPath,
        size: snapshot.size,
        sampleDocs: snapshot.docs.slice(0, 5).map((docSnap) => {
          const data = docSnap.data() as any
          return {
            id: docSnap.id,
            invoiceNumber: data.invoiceNumber || null,
            type: data.type || null,
            companyId: data.companyId || null,
            businessId: data.businessId || null,
            grandTotal: data.grandTotal ?? data.total ?? null
          }
        })
      })

      for (const invoice of serverInvoices) {
        saveToOffline(STORES.INVOICES, invoice).catch(() => {})
        saveInvoiceToLocalStorage(invoice)
      }

      setInCache(type ? `invoices_${type}_${companyId}` : `invoices_all_${companyId}`, serverInvoices, companyId)
      onInvoices(serverInvoices)
    },
    (error) => {
      console.error('FIRESTORE ERROR CODE:', (error as any)?.code || null)
      console.error('FIRESTORE ERROR MESSAGE:', error.message || String(error))
      console.error('FULL ERROR:', error)
      console.error('[Sales Firebase Debug] Invoice listener failed', {
        authUid: auth?.currentUser?.uid || null,
        companyId,
        salesCollectionPath,
        code: (error as any)?.code || null,
        message: error.message || String(error)
      })
      onError?.(error)
    }
  )
}

/**
 * Get invoices with pagination - SCALABLE VERSION
 * Uses TRUE cursor-based pagination with Firestore startAfter
 */
export async function getInvoicesPaginated(
  type?: 'sale' | 'purchase' | 'quote',
  options: PaginationOptions = {}
): Promise<PaginatedResult<Invoice>> {
  const {
    limit = 20,
    cursor,
    sortBy = 'invoiceDate',
    sortOrder = 'desc'
  } = options

  const companyId = getCurrentCompanyId()
  const userInfo = getCurrentUserInfo()

  // STEP 1: Try IndexedDB first for offline support
  let localInvoices: Invoice[] = []
  try {
    localInvoices = await getAllFromOffline<Invoice>(STORES.INVOICES)

    // Apply filters
    localInvoices = localInvoices.filter(inv => {
      const matchesType = !type || inv.type === type
      const matchesCompany = (inv as any).companyId === companyId
      return matchesType && matchesCompany
    })

    // Apply sorting
    localInvoices.sort((a, b) => {
      const aValue = (a as any)[sortBy] || (a as any).createdAt || 0
      const bValue = (b as any)[sortBy] || (b as any).createdAt || 0

      const comparison = sortOrder === 'desc'
        ? new Date(bValue).getTime() - new Date(aValue).getTime()
        : new Date(aValue).getTime() - new Date(bValue).getTime()

      return comparison
    })
  } catch (error) {
    console.warn('[getInvoicesPaginated] IndexedDB failed:', error)
  }

  // STEP 2: If offline or Firebase not ready, return local data with pagination
  if (!isDeviceOnline() || !isFirebaseReady()) {
    let startIndex = 0
    if (cursor) {
      const cursorIndex = localInvoices.findIndex(inv => inv.id === cursor)
      if (cursorIndex !== -1) {
        startIndex = cursorIndex + 1
      }
    }

    const paginatedData = filterByUserRole(localInvoices.slice(startIndex, startIndex + limit), userInfo)
    const hasMore = startIndex + limit < localInvoices.length

    return {
      data: paginatedData,
      total: localInvoices.length,
      hasMore,
      nextCursor: hasMore && paginatedData.length > 0 ? paginatedData[paginatedData.length - 1].id : undefined
    }
  }

  // STEP 3: Fetch from Firebase with TRUE cursor-based pagination
  try {
    const invoicesRef = collection(db!, COLLECTIONS.INVOICES)
    let q

    // Build base query
    const queryConstraints: any[] = []

    if (companyId) {
      queryConstraints.push(where('companyId', '==', companyId))
    }
    if (type) {
      queryConstraints.push(where('type', '==', type))
    }
    queryConstraints.push(orderBy(sortBy, sortOrder))

    // Add cursor for pagination (startAfter)
    if (cursor && lastDocumentSnapshot) {
      queryConstraints.push(startAfter(lastDocumentSnapshot))
    }

    queryConstraints.push(firestoreLimit(limit + 1)) // Fetch one extra to check hasMore

    q = query(invoicesRef, ...queryConstraints)

    const snapshot = await getDocs(q)
    let serverInvoices = snapshot.docs.map(docToInvoice)

    // Check if there are more results
    const hasMore = serverInvoices.length > limit
    if (hasMore) {
      serverInvoices = serverInvoices.slice(0, limit)
    }

    // Store last document for next page
    if (snapshot.docs.length > 0) {
      lastDocumentSnapshot = snapshot.docs[Math.min(snapshot.docs.length - 1, limit - 1)]
    }

    // Role-based filtering
    if (userInfo) {
      serverInvoices = filterByUserRole(serverInvoices, userInfo)
    }

    const nextCursor = hasMore && serverInvoices.length > 0
      ? serverInvoices[serverInvoices.length - 1].id
      : undefined

    // Update local cache asynchronously
    serverInvoices.forEach(invoice => {
      saveToOffline(STORES.INVOICES, invoice).catch(() => {})
    })

    return {
      data: serverInvoices,
      total: -1, // Firestore doesn't provide total count efficiently
      hasMore,
      nextCursor
    }
  } catch (error: any) {
    console.warn('[getInvoicesPaginated] Firebase fetch failed:', error)

    // Fallback to local data
    let startIndex = 0
    if (cursor) {
      const cursorIndex = localInvoices.findIndex(inv => inv.id === cursor)
      if (cursorIndex !== -1) {
        startIndex = cursorIndex + 1
      }
    }

    const paginatedData = filterByUserRole(localInvoices.slice(startIndex, startIndex + limit), userInfo)

    return {
      data: paginatedData,
      total: localInvoices.length,
      hasMore: startIndex + limit < localInvoices.length
    }
  }
}

/**
 * Get invoice by ID - OFFLINE FIRST
 * Checks IndexedDB first, then localStorage, then Firebase
 */
export async function getInvoiceById(id: string): Promise<Invoice | null> {
  debugLog('[getInvoiceById] Looking for invoice:', id)
  const companyId = getCurrentCompanyId()
  const userInfo = getCurrentUserInfo()
  if (!companyId) {
    return null
  }

  // STEP 1: Try IndexedDB first (instant, works offline)
  try {
    const localInvoice = await getFromOffline<Invoice>(STORES.INVOICES, id)
    if (localInvoice && (localInvoice as any).companyId === companyId) {
      if (filterByUserRole([localInvoice], userInfo).length === 0) {
        return null
      }
      debugLog('[getInvoiceById] Found in IndexedDB:', id)
      return localInvoice
    }
  } catch (error) {
    console.warn('[getInvoiceById] IndexedDB read failed:', error)
  }

  // STEP 2: Check localStorage fallback
  const invoices = getInvoicesFromLocalStorage()
  const localStorageInvoice = invoices.find(i => i.id === id && (i as any).companyId === companyId)
  if (localStorageInvoice) {
    if (filterByUserRole([localStorageInvoice], userInfo).length === 0) {
      return null
    }
    debugLog('[getInvoiceById] Found in localStorage:', id)
    return localStorageInvoice
  }

  // STEP 3: If offline or Firebase not ready, return null
  if (!isDeviceOnline() || !isFirebaseReady()) {
    debugLog('[getInvoiceById] Offline - invoice not found locally:', id)
    return null
  }

  // STEP 4: Try Firebase
  try {
    const docRef = doc(db!, COLLECTIONS.INVOICES, id)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      const invoice = docToInvoice(docSnap)
      if (filterByUserRole([invoice], userInfo).length === 0) {
        return null
      }
      // Cache in IndexedDB for future offline access
      saveToOffline(STORES.INVOICES, invoice).catch(() => {})
      debugLog('[getInvoiceById] Fetched from Firebase and cached:', id)
      return invoice
    }
    debugLog('[getInvoiceById] Not found in Firebase:', id)
    return null
  } catch (error) {
    console.error('[getInvoiceById] Firebase fetch failed:', error)
    return null
  }
}

/**
 * Create new invoice - OFFLINE FIRST
 * Always saves locally first, then syncs to server silently when online
 * No alerts, no popups - completely silent operation
 */
export async function createInvoice(invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>): Promise<Invoice | null> {
  const now = new Date().toISOString()
  const id = `invoice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  const companyId = getCurrentCompanyId()
  const userInfo = getCurrentUserInfo()
  const createdByUserName = userInfo?.displayName?.trim() || userInfo?.email?.trim()

  if (!companyId) {
    console.warn('[createInvoice] Missing companyId - cannot create invoice')
    return null
  }

  const dedupKey = buildInvoiceCreateDedupKey(invoiceData as Partial<Invoice>, companyId)
  const existingRecentInvoice = findRecentExistingInvoiceByDedupKey(dedupKey, companyId)
  if (existingRecentInvoice) {
    console.warn('[createInvoice] Duplicate create prevented (recent match):', (invoiceData as any)?.invoiceNumber)
    return existingRecentInvoice
  }

  if (inFlightInvoiceCreateKeys.has(dedupKey)) {
    const pendingInvoice = await waitForRecentInvoiceByDedupKey(dedupKey, companyId)
    if (pendingInvoice) {
      console.warn('[createInvoice] Duplicate create prevented (in-flight match):', (invoiceData as any)?.invoiceNumber)
      return pendingInvoice
    }
    console.warn('[createInvoice] Duplicate create prevented while previous request is still in progress')
    return null
  }

  inFlightInvoiceCreateKeys.add(dedupKey)
  const invoiceTypeLabel =
    (invoiceData as any)?.type === 'purchase'
      ? 'purchase'
      : (invoiceData as any)?.type === 'quote'
        ? 'quotation'
        : 'invoice'
  const stopBlocking = startBlocking({
    title: `Saving ${invoiceTypeLabel}...`,
    description: 'Please wait. Do not close the app.'
  })

  try {

  // Remove undefined values - Firestore doesn't accept them
  const cleanData = Object.fromEntries(
    Object.entries(invoiceData).filter(([_, value]) => value !== undefined)
  )

  const normalizedInput = normalizeInvoicePaymentFields(cleanData as any) as Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>

  const newInvoice: Invoice = {
    ...normalizedInput,
    id,
    createdAt: now,
    updatedAt: now,
    ...(companyId ? { companyId } : {}),
    // Track who created this invoice (for role-based filtering)
    ...(userInfo?.uid ? { createdByUserId: userInfo.uid, createdByUserRole: userInfo.role } : {}),
    ...(createdByUserName ? { createdByUserName } : {}),
    // Offline-first metadata
    _pendingSync: !isDeviceOnline(),
    _savedAt: now,
    _syncedAt: null
  } as Invoice

  // STEP 1: Always save to local IndexedDB first (instant)
  try {
    await saveToOffline(STORES.INVOICES, newInvoice)
    // Also save to localStorage for backward compatibility
    saveInvoiceToLocalStorage(newInvoice)
  } catch (error) {
    console.error('Failed to save invoice locally:', error)
  }

  // Invalidate cache after creating
  invalidateCacheByPrefix('invoices')
  // Party outstanding balances are derived from invoices.
  invalidateCacheByPrefix('parties_outstanding_')
  emitDataEvent(DATA_EVENTS.INVOICES_CHANGED, { action: 'create' })
  emitDataEvent(DATA_EVENTS.PARTIES_CHANGED, { reason: 'invoice-change' })

  // STEP 2: If online, sync to Firestore silently
  if (isDeviceOnline() && isFirebaseReady()) {
    try {
      // Sanitize data to remove undefined values before sending to Firebase
      const dataToSync = sanitizeForFirebase({
        ...normalizedInput,
        createdAt: now,
        updatedAt: now,
        ...(companyId ? { companyId } : {}),
        // Track who created this invoice (for role-based filtering)
        ...(userInfo?.uid ? { createdByUserId: userInfo.uid, createdByUserRole: userInfo.role } : {}),
        ...(createdByUserName ? { createdByUserName } : {})
      })

      const docRef = await addDoc(collection(db!, COLLECTIONS.INVOICES), dataToSync)

      // Store original offline ID before updating
      const originalOfflineId = id

      // Update local invoice with server ID and mark as synced
      newInvoice.id = docRef.id
      newInvoice._pendingSync = false
      newInvoice._syncedAt = now

      // IMPORTANT: Delete the old offline ID record BEFORE saving the new Firebase ID record
      // This prevents duplicate entries in the list
      await deleteFromOffline(STORES.INVOICES, originalOfflineId)
      deleteInvoiceFromLocalStorage(originalOfflineId)

      // Now save with the new Firebase ID
      await saveToOffline(STORES.INVOICES, newInvoice)
      saveInvoiceToLocalStorage(newInvoice)

      // Re-emit after offline->server ID replacement so list screens reload with
      // the final canonical row and do not keep temporary duplicates.
      invalidateCacheByPrefix('invoices')
      invalidateCacheByPrefix('parties_outstanding_')
      emitDataEvent(DATA_EVENTS.INVOICES_CHANGED, {
        action: 'create-sync',
        id: docRef.id,
        replacedOfflineId: originalOfflineId
      })
      emitDataEvent(DATA_EVENTS.PARTIES_CHANGED, { reason: 'invoice-change' })

      debugLog('[createInvoice] ✅ Synced to Firebase, replaced offline ID:', originalOfflineId, '→', docRef.id)

    } catch (error) {
      // Silent fail - add to sync queue for later
      debugLog('Firestore save failed, queuing for sync:', id)
      await addToSyncQueue('create', STORES.INVOICES, newInvoice)
    }
  } else {
    // Offline - add to sync queue
    await addToSyncQueue('create', STORES.INVOICES, newInvoice)
  }

  // STEP 3: Update stock for each item in the invoice
  // SALE: Deduct stock, PURCHASE: Add stock
  try {
    await updateStockForInvoice(newInvoice)
  } catch (stockError) {
    console.error('Error updating stock for invoice:', stockError)
    // Don't throw - invoice is already created
  }

    recentCreatedInvoices.set(dedupKey, {
      invoice: newInvoice,
      timestamp: Date.now()
    })

    return newInvoice
  } finally {
    stopBlocking()
    inFlightInvoiceCreateKeys.delete(dedupKey)
  }
}

/**
 * Strict Firestore invoice create for live POS checkout.
 * Creates the sale and updates item stock in one Firestore transaction so
 * saved bills and stock are shared across browsers/devices.
 */
export async function createInvoiceInFirestoreTransaction(
  invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Invoice> {
  const companyId = getCurrentCompanyId()
  const businessId = companyId
  const userInfo = getCurrentUserInfo()
  const normalizedInput = normalizeInvoicePaymentFields(invoiceData as any) as Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>
  const cartItems = Array.isArray((normalizedInput as any).items) ? (normalizedInput as any).items : []

  if (!companyId) {
    throw new Error('Missing companyId. Ask the admin to map this user to a company before billing.')
  }

  if (!isFirebaseReady() || !app || !db || !auth) {
    console.log('CHECKOUT DEBUG')
    console.log('auth.currentUser:', auth?.currentUser || null)
    console.log('uid:', auth?.currentUser?.uid)
    console.log('firebase app ready:', app)
    console.log('firestore db:', db)
    console.log('companyId/businessId:', companyId || businessId)
    console.log('cart items:', cartItems)
    console.log('bill payload:', normalizedInput)
    console.log('sales path:', `${COLLECTIONS.INVOICES}/(not-created)`)
    throw new Error(getFirebaseReadinessErrorMessage())
  }

  const currentUser = await waitForAuthenticatedUser()
  if (!currentUser) {
    console.log('CHECKOUT DEBUG')
    console.log('auth.currentUser:', auth.currentUser)
    console.log('uid:', auth.currentUser?.uid)
    console.log('firebase app ready:', app)
    console.log('firestore db:', db)
    console.log('companyId/businessId:', companyId || businessId)
    console.log('cart items:', cartItems)
    console.log('bill payload:', normalizedInput)
    console.log('sales path:', `${COLLECTIONS.INVOICES}/(not-created)`)
    throw new Error('User login is still loading. Please wait a moment and save again.')
  }

  if (!isDeviceOnline()) {
    throw new Error('You are offline. Connect to the internet before saving POS bills.')
  }

  if (cartItems.length === 0) {
    throw new Error('Cart is empty. Add at least one item before checkout.')
  }

  const now = new Date().toISOString()
  const createdByUserName = userInfo?.displayName?.trim() || userInfo?.email?.trim()
  const invoiceRef = doc(collection(db, COLLECTIONS.INVOICES))
  const salesPath = `${COLLECTIONS.INVOICES}/${invoiceRef.id}`
  const billDataForLog = {
    ...normalizedInput,
    companyId,
    createdByUserId: currentUser.uid
  }

  const canonicalInvoice = normalizeInvoicePaymentFields({
    ...normalizedInput,
    id: invoiceRef.id,
    createdAt: now,
    updatedAt: now,
    companyId,
    createdByUserId: currentUser.uid,
    ...(userInfo?.role ? { createdByUserRole: userInfo.role } : {}),
    ...(createdByUserName ? { createdByUserName } : {}),
    _pendingSync: false,
    _savedAt: now,
    _syncedAt: now
  } as any) as Invoice

  console.log('CHECKOUT DEBUG')
  console.log('auth.currentUser:', auth.currentUser)
  console.log('uid:', auth.currentUser?.uid)
  console.log('firebase app ready:', app)
  console.log('firestore db:', db)
  console.log('companyId/businessId:', companyId || businessId)
  console.log('cart items:', cartItems)
  console.log('bill payload:', billDataForLog)
  console.log('sales path:', salesPath)

  try {
    await runTransaction(db, async (transaction) => {
      const stockRows = cartItems.filter((item: any) => item?.itemId)

      const stockSnapshots: Array<{ itemRef: any; item: any; itemData: any }> = []
      for (const item of stockRows) {
        const itemId = String(item.itemId || '').trim()
        if (!itemId || itemId.startsWith('item_') || itemId.startsWith('offline_')) {
          throw new Error(`Item "${item.description || item.name || itemId}" is not saved in Firebase. Add/import the item before checkout.`)
        }

        const itemRef = doc(db!, COLLECTIONS.ITEMS, itemId)
        const itemSnap = await transaction.get(itemRef)
        if (!itemSnap.exists()) {
          throw new Error(`Item "${item.description || item.name || itemId}" was not found in Firebase.`)
        }

        const itemData = itemSnap.data() as any
        if (itemData.companyId !== companyId && itemData.businessId !== companyId) {
          throw new Error(`Item "${item.description || item.name || itemId}" does not belong to your company.`)
        }

        stockSnapshots.push({ itemRef, item, itemData })
      }

      const serverData = {
        ...sanitizeForFirebase({
          ...normalizedInput,
          companyId,
          createdByUserId: currentUser.uid,
          ...(userInfo?.role ? { createdByUserRole: userInfo.role } : {}),
          ...(createdByUserName ? { createdByUserName } : {})
        }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }

      transaction.set(invoiceRef, serverData)

      for (const { itemRef, item, itemData } of stockSnapshots) {
        const quantity = Number(item.quantity || 0)
        if (!Number.isFinite(quantity) || quantity <= 0) continue

        const currentStock = Number(itemData.stock ?? itemData.quantity ?? 0) || 0
        if ((normalizedInput as any).type !== 'purchase' && currentStock < quantity) {
          throw new Error(`Not enough stock for "${item.description || item.name || item.itemId}". Available: ${currentStock}`)
        }

        const nextStock = (normalizedInput as any).type === 'purchase'
          ? currentStock + quantity
          : currentStock - quantity

        transaction.update(itemRef, {
          ...sanitizeForFirebase({
            stock: nextStock,
            quantity: nextStock
          }),
          updatedAt: serverTimestamp()
        })
      }
    })
  } catch (error: any) {
    console.error('POS BILL SAVE ERROR CODE:', error?.code)
    console.error('POS BILL SAVE ERROR MESSAGE:', error?.message)
    console.error('FULL POS BILL SAVE ERROR:', error)
    throw error
  }

  await saveToOffline(STORES.INVOICES, canonicalInvoice).catch(() => {})
  saveInvoiceToLocalStorage(canonicalInvoice)

  invalidateCacheByPrefix('invoices')
  invalidateCacheByPrefix('items')
  invalidateCacheByPrefix('parties_outstanding_')
  emitDataEvent(DATA_EVENTS.INVOICES_CHANGED, { action: 'create-sync', id: invoiceRef.id })
  emitDataEvent(DATA_EVENTS.ITEMS_CHANGED, { action: 'stock-transaction' })
  emitDataEvent(DATA_EVENTS.PARTIES_CHANGED, { reason: 'invoice-change' })

  if (canonicalInvoice.partyId && canonicalInvoice.partyName) {
    createInvoiceLedgerEntry(
      canonicalInvoice.partyId,
      canonicalInvoice.partyName,
      canonicalInvoice.invoiceNumber,
      canonicalInvoice.invoiceDate,
      canonicalInvoice.grandTotal,
      canonicalInvoice.type
    ).catch((error) => {
      console.error('Error creating ledger entry for POS invoice:', error)
    })
  }

  recentCreatedInvoices.set(buildInvoiceCreateDedupKey(canonicalInvoice as Partial<Invoice>, companyId), {
    invoice: canonicalInvoice,
    timestamp: Date.now()
  })

  return canonicalInvoice
}

/**
 * Sync callback for offline invoices - called by offlineSyncService
 */
async function syncInvoiceToServer(syncItem: any): Promise<boolean> {
  if (syncItem.store !== STORES.INVOICES) return false

  const invoice = syncItem.data
  if (!invoice || !isFirebaseReady()) return false

  try {
    // Remove offline metadata before sending to server
    const serverData = normalizeInvoicePaymentFields({ ...invoice } as any)
    delete serverData._pendingSync
    delete serverData._savedAt
    delete serverData._syncedAt
    delete serverData.id

    const companyId = getCurrentCompanyId()
    if (companyId) {
      serverData.companyId = companyId
    }

    // Sanitize data to remove undefined values (Firebase doesn't accept undefined)
    const sanitizedData = sanitizeForFirebase(serverData)

    const docRef = await addDoc(collection(db!, COLLECTIONS.INVOICES), sanitizedData)

    // Update local with server ID
    const updatedInvoice = {
      ...invoice,
      id: docRef.id,
      _pendingSync: false,
      _syncedAt: new Date().toISOString()
    }
    await saveToOffline(STORES.INVOICES, updatedInvoice)
    saveInvoiceToLocalStorage(updatedInvoice)

    // Invalidate cache
    invalidateCacheByPrefix('invoices')
    invalidateCacheByPrefix('parties_outstanding_')
    emitDataEvent(DATA_EVENTS.INVOICES_CHANGED, { action: 'sync' })
    emitDataEvent(DATA_EVENTS.PARTIES_CHANGED, { reason: 'invoice-change' })

    return true
  } catch (error) {
    console.error('Failed to sync invoice to server:', error)
    return false
  }
}

// Register sync callback
registerSyncCallback(syncInvoiceToServer)

/**
 * Update stock quantities based on invoice type
 * SALE: Deduct stock (subtract)
 * PURCHASE: Add stock (add)
 * Also creates ledger entry for the party
 */
async function updateStockForInvoice(invoice: Invoice): Promise<void> {
  debugLog('📦 updateStockForInvoice called:', {
    type: invoice.type,
    invoiceNumber: invoice.invoiceNumber,
    itemsCount: invoice.items?.length || 0
  })

  try {
    // Update stock for each item
    if (!invoice.items || invoice.items.length === 0) {
      debugLog('⚠️ No items in invoice to update stock')
      return
    }

    for (const item of invoice.items) {
      if (item.itemId) {
        // Treat all non-purchase invoices as stock-out (sale/quotation reduce stock)
        const operation = invoice.type === 'purchase' ? 'add' : 'subtract'
        const success = await updateItemStock(item.itemId, item.quantity, operation)
        debugLog(`✅ Stock update for ${item.description}: ${success ? 'SUCCESS' : 'FAILED'}`)
      }
    }

    // Create ledger entry for the party
    if (invoice.partyId && invoice.partyName) {
      await createInvoiceLedgerEntry(
        invoice.partyId,
        invoice.partyName,
        invoice.invoiceNumber,
        invoice.invoiceDate,
        invoice.grandTotal,
        invoice.type
      )
    }
  } catch (error) {
    console.error('Error updating stock/ledger for invoice:', error)
  }
}

/**
 * Update existing invoice - OFFLINE FIRST
 * Always saves locally first, then syncs to Firebase silently when online
 */
export async function updateInvoice(id: string, updates: Partial<Invoice>): Promise<boolean> {
  debugLog('[updateInvoice] Starting offline-first update for:', id)
  const now = new Date().toISOString()
  const stopBlocking = startBlocking({
    title: 'Saving changes...',
    description: 'Please wait. Do not close the app.'
  })

  try {

  // Check if ID is offline-generated
  const isOfflineId = (invoiceId: string) => invoiceId.startsWith('invoice_') || invoiceId.startsWith('offline_')

  // STEP 1: Get existing invoice from local
  let existingInvoice: Invoice | null = null
  try {
    existingInvoice = await getFromOffline<Invoice>(STORES.INVOICES, id)
  } catch (error) {
    const localInvoices = getInvoicesFromLocalStorage()
    existingInvoice = localInvoices.find(i => i.id === id) || null
  }

  if (!existingInvoice) {
    console.error('[updateInvoice] Invoice not found:', id)
    return false
  }

    const mergedInvoice = normalizeInvoicePaymentFields({
      ...existingInvoice,
      ...updates
    } as any) as Invoice

    const updatedInvoice: Invoice = {
    ...mergedInvoice,
    updatedAt: now,
    _pendingSync: !isDeviceOnline() || isOfflineId(id),
    _savedAt: now
  } as Invoice

  // STEP 2: Always save to IndexedDB first
  try {
    await saveToOffline(STORES.INVOICES, updatedInvoice)
    updateInvoiceInLocalStorage(id, updatedInvoice)
    debugLog('[updateInvoice] ✅ Updated locally:', id)
  } catch (error) {
    console.error('[updateInvoice] Local save failed:', error)
    updateInvoiceInLocalStorage(id, updatedInvoice)
  }

  // Invalidate cache after update
  invalidateCacheByPrefix('invoices')
  invalidateCacheByPrefix('parties_outstanding_')
  emitDataEvent(DATA_EVENTS.INVOICES_CHANGED, { action: 'update', id })
  emitDataEvent(DATA_EVENTS.PARTIES_CHANGED, { reason: 'invoice-change' })

  // STEP 3: If online and not an offline-only record, sync to Firebase
  if (isDeviceOnline() && isFirebaseReady() && !isOfflineId(id)) {
    try {
      const normalizedServerUpdates = normalizeInvoicePaymentFields({
        ...existingInvoice,
        ...updates
      } as any)
      const serverDataRaw: any = {
        ...updates,
        updatedAt: now
      }
      if ((updates as any).payment !== undefined) {
        serverDataRaw.payment = normalizedServerUpdates.payment
      }
      if ((updates as any).payment !== undefined || (updates as any).paymentStatus !== undefined) {
        serverDataRaw.paymentStatus = normalizedServerUpdates.paymentStatus
      }
      const serverData = sanitizeForFirebase(serverDataRaw)
      const docRef = doc(db!, COLLECTIONS.INVOICES, id)
      await updateDoc(docRef, serverData)

      // Mark as synced
      const syncedInvoice = {
        ...updatedInvoice,
        _pendingSync: false,
        _syncedAt: now
      }
      await saveToOffline(STORES.INVOICES, syncedInvoice)

      debugLog('[updateInvoice] ✅ Synced to Firebase:', id)
    } catch (error) {
      console.warn('[updateInvoice] Firebase sync failed, queuing:', error)
      await addToSyncQueue('update', STORES.INVOICES, updatedInvoice)
    }
  } else if (!isOfflineId(id)) {
    await addToSyncQueue('update', STORES.INVOICES, updatedInvoice)
  }

  return true
  } finally {
    stopBlocking()
  }
}

/**
 * Delete invoice - OFFLINE FIRST
 * Always deletes locally first, then syncs to Firebase when online
 */
export async function deleteInvoice(id: string): Promise<boolean> {
  debugLog('[deleteInvoice] Starting offline-first deletion for:', id)

  // Check if ID is offline-generated (not yet synced to Firebase)
  const isOfflineId = id.startsWith('invoice_') || id.startsWith('offline_')

  // STEP 1: Always delete from IndexedDB first
  try {
    await deleteFromOffline(STORES.INVOICES, id)
    debugLog('[deleteInvoice] ✅ Deleted from IndexedDB:', id)
  } catch (error) {
    console.warn('[deleteInvoice] IndexedDB delete failed:', error)
  }

  // STEP 2: Delete from localStorage
  deleteInvoiceFromLocalStorage(id)

  // Invalidate cache after delete
  invalidateCacheByPrefix('invoices')
  invalidateCacheByPrefix('parties_outstanding_')
  emitDataEvent(DATA_EVENTS.INVOICES_CHANGED, { action: 'delete', id })
  emitDataEvent(DATA_EVENTS.PARTIES_CHANGED, { reason: 'invoice-change' })

  // STEP 3: If online and has Firebase ID, sync deletion
  if (isDeviceOnline() && isFirebaseReady() && !isOfflineId) {
    try {
      const docRef = doc(db!, COLLECTIONS.INVOICES, id)
      await deleteDoc(docRef)
      debugLog('[deleteInvoice] ✅ Deleted from Firebase:', id)
    } catch (error) {
      console.warn('[deleteInvoice] Firebase delete failed, queuing:', error)
      await addToSyncQueue('delete', STORES.INVOICES, { id })
    }
  } else if (!isOfflineId) {
    // Queue for sync if has Firebase ID but currently offline
    await addToSyncQueue('delete', STORES.INVOICES, { id })
  }

  return true
}

/**
 * Process scanned invoice data and create invoice with auto-created parties and items
 */
export async function processScannedInvoice(
  scannedData: ScannedInvoiceData,
  type: 'sale' | 'purchase',
  businessGSTIN: string = '33AAAAA0000A1Z5' // Replace with actual business GSTIN
): Promise<{ invoice: Invoice | null; party: any; items: any[] }> {
  try {
    const currentUser = getCurrentUserInfo()
    const billingStaffName = currentUser?.displayName?.trim() || currentUser?.email?.trim() || null

    // Determine party based on invoice type
    const partyData = type === 'purchase' ? scannedData.vendor : scannedData.buyer || scannedData.vendor
    const partyType = type === 'purchase' ? 'supplier' : 'customer'

    // Step 1: Find or create party
    const party = await findOrCreatePartyFromInvoice(
      partyData.name,
      partyData.gstin,
      partyData.phone,
      partyData.email,
      partyData.address,
      partyData.city,
      partyData.state,
      partyData.pinCode,
      partyType
    )

    if (!party) {
      throw new Error('Failed to create/find party')
    }

    // Step 2: Find or create items
    const processedItems = []
    const invoiceItems = []
    const explicitTaxRate =
      (scannedData.cgstRate || 0) +
      (scannedData.sgstRate || 0) +
      (scannedData.igstRate || 0)
    const inferredTaxRate =
      scannedData.taxableValue > 0 && scannedData.totalTaxAmount > 0
        ? Math.round((scannedData.totalTaxAmount / scannedData.taxableValue) * 10000) / 100
        : 0
    const scannedTaxRate = explicitTaxRate || inferredTaxRate

    for (const scannedItem of scannedData.items) {
      const quantity = scannedItem.quantity || 1
      const scannedTaxableAmount = scannedItem.taxableAmount || 0
      const lineRate = scannedItem.rate || (scannedTaxableAmount > 0 ? scannedTaxableAmount / quantity : 0)
      const lineTaxableAmount = scannedTaxableAmount || lineRate * quantity
      const lineCgstAmount = scannedItem.cgstAmount || 0
      const lineSgstAmount = scannedItem.sgstAmount || 0
      const lineIgstAmount = scannedItem.igstAmount || 0
      const hasLineTax =
        lineCgstAmount > 0 ||
        lineSgstAmount > 0 ||
        lineIgstAmount > 0 ||
        (scannedItem.cgstRate || 0) > 0 ||
        (scannedItem.sgstRate || 0) > 0 ||
        (scannedItem.igstRate || 0) > 0
      const lineCgstRate = scannedItem.cgstRate || (lineTaxableAmount > 0 && lineCgstAmount > 0 ? Math.round((lineCgstAmount / lineTaxableAmount) * 10000) / 100 : 0)
      const lineSgstRate = scannedItem.sgstRate || (lineTaxableAmount > 0 && lineSgstAmount > 0 ? Math.round((lineSgstAmount / lineTaxableAmount) * 10000) / 100 : 0)
      const lineIgstRate = scannedItem.igstRate || (lineTaxableAmount > 0 && lineIgstAmount > 0 ? Math.round((lineIgstAmount / lineTaxableAmount) * 10000) / 100 : 0)
      const taxRate = lineCgstRate + lineSgstRate + lineIgstRate || scannedTaxRate

      const item = await findOrCreateItemFromInvoice(
        scannedItem.description,
        scannedItem.hsnCode,
        lineRate,
        scannedItem.unit,
        taxRate
      )

      if (item) {
        processedItems.push(item)

        // Calculate tax for this item
        const partyStateCode = party.gstDetails?.stateCode || '33'
        const businessStateCode = businessGSTIN.substring(0, 2)

        const taxCalc = calculateGST({
          amount: lineRate,
          taxRate,
          quantity,
          sellerStateCode: type === 'purchase' ? partyStateCode : businessStateCode,
          buyerStateCode: type === 'purchase' ? businessStateCode : partyStateCode
        })

        const totalLineTax = lineCgstAmount + lineSgstAmount + lineIgstAmount
        const lineTotalAmount = scannedItem.totalAmount || scannedItem.amount || lineTaxableAmount + totalLineTax

        invoiceItems.push({
          id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          itemId: item.id,
          itemName: item.name,
          description: scannedItem.description,
          hsnCode: scannedItem.hsnCode,
          quantity,
          unit: scannedItem.unit,
          rate: lineRate,
          discountPercent: 0,
          discountAmount: 0,
          taxableAmount: hasLineTax ? lineTaxableAmount : taxCalc.taxableAmount,
          cgstPercent: hasLineTax ? lineCgstRate : taxCalc.cgst,
          cgstAmount: hasLineTax ? lineCgstAmount : taxCalc.cgstAmount,
          sgstPercent: hasLineTax ? lineSgstRate : taxCalc.sgst,
          sgstAmount: hasLineTax ? lineSgstAmount : taxCalc.sgstAmount,
          igstPercent: hasLineTax ? lineIgstRate : taxCalc.igst,
          igstAmount: hasLineTax ? lineIgstAmount : taxCalc.igstAmount,
          totalAmount: hasLineTax ? lineTotalAmount : taxCalc.totalAmount
        })
      }
    }

    // Step 3: Calculate invoice totals
    const totals = calculateInvoiceTotals({
      items: invoiceItems.map(i => ({
        amount: i.taxableAmount,
        cgstAmount: i.cgstAmount,
        sgstAmount: i.sgstAmount,
        igstAmount: i.igstAmount
      }))
    })

    // Step 4: Create invoice
    const invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'> = {
      type,
      invoiceNumber: scannedData.invoiceNumber,
      invoiceDate: scannedData.invoiceDate,
      partyId: party.id,
      partyName: party.companyName,
      partyGSTIN: party.gstDetails?.gstin,
      partyAddress: party.billingAddress,
      partyPhone: party.phone,
      partyEmail: party.email,
      businessName: 'Anna ERP',
      businessGSTIN,
      businessAddress: {
        street: '',
        city: '',
        state: '',
        pinCode: '',
        country: 'India'
      },
      businessPhone: '',
      businessEmail: '',
      businessStateCode: businessGSTIN.substring(0, 2),
      referenceNumber: scannedData.referenceNumber,
      buyerOrderNumber: scannedData.buyerOrderNumber,
      deliveryNoteNumber: scannedData.deliveryNoteNumber,
      items: invoiceItems,
      subtotal: totals.subtotal,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: totals.taxableAmount,
      cgstAmount: totals.cgstTotal,
      sgstAmount: totals.sgstTotal,
      igstAmount: totals.igstTotal,
      cessAmount: 0,
      totalTaxAmount: totals.totalTax,
      shippingCharges: 0,
      otherCharges: 0,
      roundOff: scannedData.roundOff,
      grandTotal: scannedData.grandTotal,
      payment: {
        mode: scannedData.paymentMode === 'Mode/Terms of Payment' ? 'cash' : 'cash',
        status: 'unpaid',
        paidAmount: 0,
        dueAmount: scannedData.grandTotal
      },
      transport: scannedData.vehicleNumber ? {
        vehicleNumber: scannedData.vehicleNumber,
        destination: scannedData.destination
      } : undefined,
      termsOfDelivery: scannedData.termsOfPayment,
      status: 'approved',
      createdBy: billingStaffName || 'AI Scanner',

      // Billing Staff (Accountant) metadata from login
      billingStaffId: currentUser?.uid || null,
      billingStaffName,
      billingStaffRole: currentUser?.role || null,
      billingStaffEmail: currentUser?.email?.trim() || null
    }

    const invoice = await createInvoice(invoiceData)

    return {
      invoice,
      party,
      items: processedItems
    }
  } catch (error) {
    console.error('Error processing scanned invoice:', error)
    return {
      invoice: null,
      party: null,
      items: []
    }
  }
}

// Helper function to convert Firestore document to Invoice
function docToInvoice(doc: QueryDocumentSnapshot<DocumentData>): Invoice {
  const data = normalizeInvoicePaymentFields(doc.data() as any)
  return {
    ...data,
    id: doc.id
  } as Invoice
}

// ============================================
// LOCAL STORAGE FALLBACK FUNCTIONS
// ============================================

function getInvoicesFromLocalStorage(type?: 'sale' | 'purchase' | 'quote', limitCount?: number): Invoice[] {
  try {
    const companyId = getCurrentCompanyId()
    if (!companyId) return []

    const stored = getLocalJSON<Invoice[]>(getLocalStorageKey(), [])
    let invoices: Invoice[] = Array.isArray(stored)
      ? stored.map(inv => normalizeInvoicePaymentFields(inv as any) as Invoice)
      : []

    invoices = invoices.filter(i => (i as any).companyId === companyId)

    if (type) {
      invoices = invoices.filter(i => i.type === type)
    }

    // Sort by date descending
    invoices.sort((a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime())
    invoices = dedupeInvoicesByIdentity(invoices, companyId)

    if (limitCount) {
      invoices = invoices.slice(0, limitCount)
    }

    return invoices
  } catch (error) {
    console.error('Error reading invoices from local storage:', error)
    return []
  }
}

function saveInvoiceToLocalStorage(invoice: Invoice): void {
  try {
    const storageKey = getLocalStorageKey()
    const invoices = getMutableLocalJSON<Invoice[]>(storageKey, [])
    if (!Array.isArray(invoices)) return
    const normalizedInvoice = normalizeInvoicePaymentFields(invoice as any) as Invoice
    const existingIndex = invoices.findIndex(existing => existing.id === normalizedInvoice.id)
    if (existingIndex >= 0) {
      invoices[existingIndex] = normalizedInvoice
    } else {
      invoices.push(normalizedInvoice)
    }
    markLocalJSONDirty(storageKey)
  } catch (error) {
    console.error('Error saving invoice to local storage:', error)
  }
}

function updateInvoiceInLocalStorage(id: string, updates: Partial<Invoice>): boolean {
  try {
    const storageKey = getLocalStorageKey()
    const invoices = getMutableLocalJSON<Invoice[]>(storageKey, [])
    if (!Array.isArray(invoices)) return false
    const index = invoices.findIndex(i => i.id === id)

    if (index === -1) return false

    invoices[index] = normalizeInvoicePaymentFields({ ...invoices[index], ...updates } as any) as Invoice
    markLocalJSONDirty(storageKey)
    return true
  } catch (error) {
    console.error('Error updating invoice in local storage:', error)
    return false
  }
}

function deleteInvoiceFromLocalStorage(id: string): boolean {
  try {
    const storageKey = getLocalStorageKey()
    const invoices = getMutableLocalJSON<Invoice[]>(storageKey, [])
    if (!Array.isArray(invoices)) return false
    const idx = invoices.findIndex(i => i.id === id)
    if (idx === -1) return false
    invoices.splice(idx, 1)
    markLocalJSONDirty(storageKey)
    return true
  } catch (error) {
    console.error('Error deleting invoice from local storage:', error)
    return false
  }
}
